import AddEditLoanDialog from "@/components/Forms/AddEditLoanDialog.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useDeleteParty } from "@/mutations/useDeleteParty.tsx";
import { type MrtParty, useFetchMrtPartiesForCase } from "@/queries/useFetchMrtPartiesForCase.ts";
import { getErrorMessage } from "@/utils/api.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_SortingState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";

type Props = {
    caseIdUUID: string;
    roleName?: string;
};

const MrtLoansTable = ({ caseIdUUID }: Props) => {
    const { hasPermission } = useEmployee();
    const deleteParty = useDeleteParty();
    const roleName = ["Loan"];
    const [editPartyId, setEditPartyId] = useState<string | undefined>();
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });

    const { data, isError, isRefetching, isLoading } = useFetchMrtPartiesForCase({
        caseIdUUID,
        roleName,
        columnFilters,
        globalFilter,
        pagination,
        sorting,
    });

    const handleEditRow = (idUUID: string) => {
        setEditPartyId(idUUID);
    };

    const handleDeleteRow = (row: MRT_Row<MrtParty>) => {
        if (window.confirm("Are you sure you want to delete this Loan?")) {
            deleteParty.mutate(
                {
                    partyUUID: row.original.idUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Loan removed.", { variant: "success" });
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Loan could not be removed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        }
    };

    const columns = useMemo<MRT_ColumnDef<MrtParty>[]>(() => {
        const columnDefs: MRT_ColumnDef<MrtParty>[] = [
            {
                accessorKey: "edit",
                header: "",
                enableSorting: false,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditRow(row.original.idUUID);
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                ),
                size: 50,
            },
            {
                accessorKey: "companyName",
                header: "Company",
            },
            {
                accessorKey: "checkAmountMM",
                header: "Loan Amount",
            },
            {
                accessorKey: "loanDate",
                header: "Loan Date",
            },
            {
                accessorKey: "loanPayoffAmount",
                header: "Payoff Amount",
            },
            {
                accessorKey: "loanPayoffDate",
                header: "Payoff Date",
            },
        ];

        if (hasPermission("delete:loan")) {
            columnDefs.push({
                accessorKey: "delete",
                header: "Delete",
                size: 50,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleDeleteRow(row);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            });
        }

        return columnDefs;
    }, [hasPermission, handleEditRow, handleEditRow, handleDeleteRow]);

    const table = useMaterialReactTable({
        columns,
        data: isLoading || isRefetching || isError || data === undefined ? [] : data.data,
        initialState: {
            showColumnFilters: false,
        },
        enableTopToolbar: isError,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading data",
              }
            : undefined,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        // renderTopToolbarCustomActions: () => (
        //     <Tooltip arrow title="Refresh Data">
        //         <IconButton onClick={() => refetch()}>
        //             <RefreshIcon />
        //         </IconButton>
        //     </Tooltip>
        // ),
        rowCount: data?.meta?.totalRowCount ?? 0,
        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading || isRefetching,
            sorting,
        },
    });

    return (
        <>
            <MaterialReactTable table={table} />
            <AddEditLoanDialog
                caseUUID={caseIdUUID}
                showAddButton={false}
                editPartyId={editPartyId}
                clearEditPartyId={() => {
                    setEditPartyId(undefined);
                }}
            />
        </>
    );
};

export default MrtLoansTable;
